import React, { useCallback, useEffect } from 'react';
import { Row, Col, Card, Statistic, Spin, Alert, Button, List, Tag, Empty } from 'antd';
import { 
  DesktopOutlined, 
  PlayCircleOutlined, 
  ClockCircleOutlined, 
  ExclamationCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { fetchOverview, fetchHealth, fetchMetrics } from '../../store/slices/monitoringSlice';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import TaskTrendChart from '../../components/Charts/TaskTrendChart';
import GpuStatusChart from '../../components/Charts/GpuStatusChart';
import TaskStatusChart from '../../components/Charts/TaskStatusChart';

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { overview, metrics, loading, error } = useSelector((state: RootState) => state.monitoring);
  const { autoRefresh, refreshInterval } = useSelector((state: RootState) => state.settings);

  const refreshData = useCallback(() => {
    dispatch(fetchOverview());
    dispatch(fetchHealth());
    dispatch(fetchMetrics());
  }, [dispatch]);

  // 自动刷新
  useAutoRefresh({
    interval: refreshInterval,
    enabled: autoRefresh,
    onRefresh: refreshData,
  });

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // 生成模拟的趋势数据
  const generateTrendData = () => {
    const labels = [];
    const completed = [];
    const failed = [];
    
    for (let i = 0; i < 24; i++) {
      const hour = String(i).padStart(2, '0');
      labels.push(`${hour}:00`);
      completed.push(Math.floor(Math.random() * 50) + 10);
      failed.push(Math.floor(Math.random() * 10) + 1);
    }
    
    return { labels, completed, failed };
  };

  const trendData = generateTrendData();

  // 生成模拟的关键指标数据（当API数据不可用时）
  const mockMetrics = {
    active_gpus: -1,
    total_tasks: -1,
    queue_length: -1,
    failed_tasks: -1,
    completed_tasks: -1,
    processing_tasks: -1,
    queued_tasks: -1,
  };

  // 提取队列信息
  const getQueueOverview = () => {
    if (!overview?.metrics) return [];
    
    return Object.keys(overview.metrics)
      .filter(key => key.startsWith('queue_length_'))
      .map(key => {
        const queueName = key.replace('queue_length_', '');
        return {
          name: queueName,
          length: (overview.metrics as any)[key] || 0
        };
      })
      .filter(queue => queue.length > 0) // 只显示有任务的队列
      .sort((a, b) => b.length - a.length); // 按任务数量降序排列
  };

  const queueOverview = getQueueOverview();

  if (loading && !overview) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="数据加载失败"
        description={error}
        type="error"
        action={
          <Button size="small" onClick={refreshData}>
            重试
          </Button>
        }
        showIcon
      />
    );
  }

  return (
    <div>
      <div style={{ 
        marginBottom: 16, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8
      }}>
        <h1 style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '18px' : '24px' }}>系统概览</h1>
        <Button 
          type="primary" 
          icon={<ReloadOutlined />} 
          onClick={refreshData}
          loading={loading}
          size={window.innerWidth <= 768 ? 'small' : 'middle'}
        >
          刷新数据
        </Button>
      </div>

      {/* 关键指标卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="活跃GPU"
              value={(overview?.metrics as any)?.gpu_active_count || metrics?.gpu_status_counts?.ACTIVE || mockMetrics.active_gpus}
              prefix={<DesktopOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总任务数"
              value={
                overview?.metrics ? 
                Object.keys(overview.metrics)
                  .filter(key => key.startsWith('queue_length_'))
                  .reduce((sum, key) => sum + ((overview.metrics as any)[key] || 0), 0) :
                metrics?.task_status_counts ? 
                (metrics.task_status_counts.QUEUED || 0) + 
                (metrics.task_status_counts.PROCESSING || 0) + 
                (metrics.task_status_counts.COMPLETED || 0) + 
                (metrics.task_status_counts.FAILED || 0) + 
                (metrics.task_status_counts.TIMEOUT || 0) :
                mockMetrics.total_tasks
              }
              prefix={<PlayCircleOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="队列长度"
              value={
                overview?.metrics ? 
                Object.keys(overview.metrics)
                  .filter(key => key.startsWith('queue_length_'))
                  .reduce((sum, key) => sum + ((overview.metrics as any)[key] || 0), 0) :
                metrics?.task_status_counts?.QUEUED || 
                mockMetrics.queue_length
              }
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="故障GPU"
              value={
                (overview?.metrics as any)?.gpu_faulty_count || 
                metrics?.gpu_status_counts?.FAULTY || 
                mockMetrics.failed_tasks
              }
              prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="任务执行趋势" style={{ height: window.innerWidth <= 768 ? 300 : 400 }}>
            <TaskTrendChart data={trendData} height={window.innerWidth <= 768 ? 200 : 300} />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="GPU状态分布" style={{ height: window.innerWidth <= 768 ? 300 : 400 }}>
            <GpuStatusChart 
              data={{
                ACTIVE: metrics?.gpu_status_counts?.ACTIVE || mockMetrics.active_gpus,
                OFFLINE: metrics?.gpu_status_counts?.OFFLINE || 2,
                FAULTY: metrics?.gpu_status_counts?.FAULTY || 1,
              }} 
              height={window.innerWidth <= 768 ? 200 : 300} 
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="任务状态分布" style={{ height: window.innerWidth <= 768 ? 300 : 400 }}>
            <TaskStatusChart 
              data={{
                QUEUED: metrics?.task_status_counts?.QUEUED || mockMetrics.queued_tasks,
                PROCESSING: metrics?.task_status_counts?.PROCESSING || mockMetrics.processing_tasks,
                COMPLETED: metrics?.task_status_counts?.COMPLETED || mockMetrics.completed_tasks,
                FAILED: metrics?.task_status_counts?.FAILED || mockMetrics.failed_tasks,
                TIMEOUT: metrics?.task_status_counts?.TIMEOUT || 0,
              }} 
              height={window.innerWidth <= 768 ? 200 : 300} 
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="队列概览" style={{ height: window.innerWidth <= 768 ? 300 : 400 }}>
            <div style={{ 
              padding: window.innerWidth <= 768 ? '12px' : '20px', 
              height: '100%', 
              overflow: 'auto' 
            }}>
              {queueOverview.length > 0 ? (
                <List
                  dataSource={queueOverview}
                  renderItem={(queue) => (
                    <List.Item>
                      <div style={{ 
                        width: '100%', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 8
                      }}>
                        <div>
                          <Tag color="blue" style={{ marginRight: 8, fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}>
                            {queue.name}
                          </Tag>
                        </div>
                        <div>
                          <Statistic
                            value={queue.length}
                            valueStyle={{ 
                              fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                              color: queue.length > 10 ? '#ff4d4f' : queue.length > 5 ? '#faad14' : '#52c41a'
                            }}
                            suffix="个任务"
                          />
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty 
                  description="暂无队列任务" 
                  style={{ marginTop: '50px' }}
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
